const CODE_TAG_REGEX = /<code:(\w+)( type="workflow")?>([\s\S]*?)<\/code:\1>/g;
const CODE_TAG_START_REGEX = /<code:(\w+)( type="workflow")?>/;
const TOOL_TAG_REGEX = /<tool>([\s\S]*?)<\/tool>/g;
const TOOL_TAG_START_REGEX = /<tool>/;
const TOOL_TAG_END = "</tool>";
const PRODUCT_TAG_REGEX = /<product\s+pid="([^"]+)"[^>]*>([\s\S]*?)<\/product>/g;
const PRODUCT_TAG_START_REGEX = /<product\b/;
const PRODUCT_TAG_END = "</product>";
const SUPPORT_TAG_REGEX = /<support\/>/g;
const SUPPORT_SUMMARIZATION_TAG_REGEX = /<support_follow_up_prompt>([\s\S]*?)<\/support_follow_up_prompt>/g;
const FENCE_LINE_REGEX = /^ {0,3}(`{3,}|~{3,})/gm;
const FOLLOW_UP_PROMPTS_TAG_START = "<follow_up_prompts>";
const FOLLOW_UP_PROMPTS_TAG_END = "</follow_up_prompts>";

const transformCodeBlock = (content: string) =>
    content.replace(CODE_TAG_REGEX, (_, lang, _type, code) => {
        const formattedLang = _type ? `${lang}-workflow` : lang;
        return `\n~~~${formattedLang}\n${code.trim()}\n~~~\n`;
    });

const transformToolTag = (content: string) =>
    content.replace(TOOL_TAG_REGEX, (_, toolName: string) => {
        const name = toolName.trim();
        return name ? `\`tool:${name}\`\n\n` : "";
    });

const transformProductTag = (content: string) =>
    content.replace(
        PRODUCT_TAG_REGEX,
        (_, productId: string, code: string) => `\`product:${productId.trim()}:${code.trim()}\``
    );

const transformSupportTag = (content: string) => content.replace(SUPPORT_TAG_REGEX, `\`support\``);

const transformSummarizationTag = (content: string) => content.replace(SUPPORT_SUMMARIZATION_TAG_REGEX, `\`summary\``);

const applyTransformers =
    (...transformers: Array<(content: string) => string>) =>
    (content: string) =>
        transformers.reduce((accumulator, transformer) => transformer(accumulator), content);

export const transformContent = applyTransformers(
    transformCodeBlock,
    transformToolTag,
    transformProductTag,
    transformSupportTag,
    transformSummarizationTag
);

export interface FollowUpPromptsSplit {
    text: string;
    prompts: string[];
}

export const splitFollowUpPrompts = (content: string): FollowUpPromptsSplit => {
    const start = content.indexOf(FOLLOW_UP_PROMPTS_TAG_START);
    if (start === -1) {
        return { text: content, prompts: [] };
    }
    const before = content.slice(0, start).trimEnd();
    const end = content.indexOf(FOLLOW_UP_PROMPTS_TAG_END, start);
    if (end === -1) {
        return { text: before, prompts: [] };
    }
    const after = content.slice(end + FOLLOW_UP_PROMPTS_TAG_END.length).trimStart();
    const prompts = content
        .slice(start + FOLLOW_UP_PROMPTS_TAG_START.length, end)
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line !== "");
    return { text: after === "" ? before : `${before}\n\n${after}`, prompts };
};

const WORKFLOW_FENCE_REGEX = /~~~\w+-workflow\n([\s\S]*?)\n~~~/g;

export interface WorkflowBlock {
    match: string;
    json: Record<string, unknown>;
}

export const extractWorkflowBlocks = (content: string): WorkflowBlock[] => {
    const blocks: WorkflowBlock[] = [];
    for (const match of content.matchAll(WORKFLOW_FENCE_REGEX)) {
        try {
            blocks.push({ match: match[0], json: JSON.parse(match[1]) as Record<string, unknown> });
        } catch {
            // Not valid JSON - leave it displayed as-is.
        }
    }
    return blocks;
};

export const streamTagContent = (content: string) => {
    const unclosedToolTagStart = content.search(TOOL_TAG_START_REGEX);
    const isToolTagUnclosed =
        unclosedToolTagStart !== -1 && !content.slice(unclosedToolTagStart).includes(TOOL_TAG_END);
    const unclosedProductTagStart = content.search(PRODUCT_TAG_START_REGEX);
    const isProductTagUnclosed =
        unclosedProductTagStart !== -1 && !content.slice(unclosedProductTagStart).includes(PRODUCT_TAG_END);
    const codeTagStart = content.search(CODE_TAG_START_REGEX);

    if (
        isToolTagUnclosed &&
        (!isProductTagUnclosed || unclosedToolTagStart <= unclosedProductTagStart) &&
        (codeTagStart === -1 || unclosedToolTagStart <= codeTagStart)
    ) {
        return content.slice(0, unclosedToolTagStart);
    }

    if (isProductTagUnclosed && (codeTagStart === -1 || unclosedProductTagStart <= codeTagStart)) {
        return content.slice(0, unclosedProductTagStart);
    }

    if (codeTagStart === -1) {
        return content.replace(/<[^>]*$/, "");
    }
    const matchingTag = content.slice(codeTagStart).match(CODE_TAG_START_REGEX);
    const lang = matchingTag?.[1];
    if (!lang) {
        return content.slice(0, codeTagStart);
    }
    const formattedLang = matchingTag?.[2] ? `${lang}-workflow` : lang;
    const partialContent = content.slice(codeTagStart + matchingTag![0].length);
    return `${content.slice(0, codeTagStart)}\n~~~${formattedLang}\n${partialContent}`;
};

// Returns the earliest index where an inline span (code, bold, italic, or link) is left open.
const findSafeInlineCutoff = (text: string): number => {
    let index = 0;
    let inCodeSpan = false;
    let codeSpanStart = 0;
    let codeSpanTickLength = 0;
    const boldStack: number[] = [];
    const underlineStack: number[] = [];
    const italicStarStack: number[] = [];
    const italicUnderscoreStack: number[] = [];
    let openBracketStart: number | undefined;
    let openParenAfterBracket = false;

    while (index < text.length) {
        const char = text[index];

        if (char === "`") {
            const start = index;
            let length = 0;
            while (text[index] === "`") {
                length += 1;
                index += 1;
            }
            if (inCodeSpan) {
                if (length === codeSpanTickLength) {
                    inCodeSpan = false;
                }
            } else {
                inCodeSpan = true;
                codeSpanStart = start;
                codeSpanTickLength = length;
            }
            continue;
        }

        if (inCodeSpan) {
            index += 1;
            continue;
        }

        if (char === "*" || char === "_") {
            const marker = char;
            const start = index;
            let length = 0;
            while (text[index] === marker) {
                length += 1;
                index += 1;
            }
            const isListBullet =
                marker === "*" && length === 1 && text[index] === " " && (start === 0 || text[start - 1] === "\n");
            if (isListBullet) {
                continue;
            }
            const doubleStack = marker === "*" ? boldStack : underlineStack;
            const singleStack = marker === "*" ? italicStarStack : italicUnderscoreStack;
            let remaining = length;
            while (remaining >= 2) {
                if (doubleStack.length > 0) {
                    doubleStack.pop();
                } else {
                    doubleStack.push(start);
                }
                remaining -= 2;
            }
            if (remaining === 1) {
                if (singleStack.length > 0) {
                    singleStack.pop();
                } else {
                    singleStack.push(start + length - 1);
                }
            }
            continue;
        }

        if (char === "[") {
            openBracketStart = index;
            openParenAfterBracket = false;
            index += 1;
            continue;
        }

        if (char === "]" && openBracketStart !== undefined) {
            if (text[index + 1] === "(") {
                openParenAfterBracket = true;
                index += 2;
            } else {
                openBracketStart = undefined;
                index += 1;
            }
            continue;
        }

        if (char === ")" && openBracketStart !== undefined && openParenAfterBracket) {
            openBracketStart = undefined;
            openParenAfterBracket = false;
            index += 1;
            continue;
        }

        index += 1;
    }

    const openStarts = [
        ...(inCodeSpan ? [codeSpanStart] : []),
        ...boldStack,
        ...underlineStack,
        ...italicStarStack,
        ...italicUnderscoreStack,
        ...(openBracketStart !== undefined ? [openBracketStart] : []),
    ];

    return openStarts.length === 0 ? text.length : Math.min(...openStarts);
};

// Trims trailing markdown left mid-span so react-markdown never renders half-finished syntax.
// An open fence is left untrimmed since CodeBlock shows its own loading state for it. Callers
// must stop trimming once streaming ends so the full content always ends up shown.
export const getSafeMarkdown = (content: string): string => {
    const fenceMatches = [...content.matchAll(FENCE_LINE_REGEX)];
    if (fenceMatches.length % 2 !== 0) {
        return content;
    }
    const lastFence = fenceMatches[fenceMatches.length - 1];
    const safeHeadEnd = lastFence ? lastFence.index! + lastFence[0].length : 0;
    const tail = content.slice(safeHeadEnd);
    const tailCutoff = findSafeInlineCutoff(tail);
    return content.slice(0, safeHeadEnd + tailCutoff);
};
